import React, { Component, Fragment } from 'react';
import { defineMessages, injectIntl } from 'react-intl-next';
import Flag, { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import SuccessIcon from '@atlaskit/icon/core/success';
import InfoIcon from '@atlaskit/icon/core/information';
import WarningIcon from '@atlaskit/icon/core/warning';
// AFP-2532 TODO: Fix automatic suppressions below
// eslint-disable-next-line @atlassian/tangerine/import/entry-points
import { colors } from '@atlaskit/theme';
import ViewTracker from '../../common/analytics/view-tracker';
import { type Props } from './types';
import { token } from '@atlaskit/tokens';
import { ContextualAnalyticsData, MODAL } from '@atlassian/analytics-bridge';
import { fg } from '@atlaskit/platform-feature-flags';

export const messages = defineMessages({
	'generic-error-heading': {
		id: 'project-pages.generic-error.heading',
		defaultMessage: 'Well, this is awkward...',
		description: '',
	},
	'generic-error-description': {
		id: 'project-pages.generic-error.description',
		defaultMessage: 'Something went wrong. Please try again later.',
		description: '',
	},
	'generic-error-reload-button': {
		id: 'project-pages.generic-error.button.reload',
		defaultMessage: 'Reload page',
		description: '',
	},
	'generic-success-flag-heading-space': {
		id: 'project-pages.generic-success-flag.heading.space',
		defaultMessage: 'Space connected',
		description: '',
	},
	'generic-success-flag-heading-page': {
		id: 'project-pages.generic-success-flag.heading.page',
		defaultMessage: 'Page connected',
		description: '',
	},
	'generic-success-flag-heading-live-doc': {
		id: 'project-pages.generic-success-flag.heading.live-doc',
		defaultMessage: 'Live doc connected',
		description: 'Heading to show on success flag when connected to a live doc',
	},
	'generic-success-flag-description-space': {
		id: 'project-pages.generic-success-flag.description.space',
		defaultMessage: 'Successfully connected with {connectedSpaceOrPageName} space.',
		description: '',
	},
	'generic-success-flag-description-page': {
		id: 'project-pages.generic-success-flag.description.page',
		defaultMessage: 'Successfully connected with {connectedSpaceOrPageName} page.',
		description: '',
	},
	'generic-success-flag-description-live-doc': {
		id: 'project-pages.generic-success-flag.description.live-doc',
		defaultMessage: 'Successfully connected with {connectedSpaceOrPageName} live doc.',
		description: 'Description to show on success flag when connected to a live doc',
	},
	'generic-activation-error-flag-description': {
		id: 'project-pages.generic-info-flag.description',
		defaultMessage:
			'Something went wrong. Please try again or contact support if the issue persists',
		description:
			'Description in an informative flag, prompting users to try again or contact support if the issue persists',
	},
});

class Flags extends Component<Props> {
	getSuccessFlagHeading = () => {
		const { isConnectedToPage, isConnectedToLiveDoc } = this.props;
		if (isConnectedToPage) {
			if (isConnectedToLiveDoc && fg('confluence_live_pages_enabled_in_jira')) {
				return messages['generic-success-flag-heading-live-doc'];
			}
			return messages['generic-success-flag-heading-page'];
		}
		return messages['generic-success-flag-heading-space'];
	};

	getSuccessFlagDescription = () => {
		const { isConnectedToPage, isConnectedToLiveDoc } = this.props;
		if (isConnectedToPage) {
			if (isConnectedToLiveDoc && fg('confluence_live_pages_enabled_in_jira')) {
				return messages['generic-success-flag-description-live-doc'];
			}
			return messages['generic-success-flag-description-page'];
		}
		return messages['generic-success-flag-description-space'];
	};

	render() {
		const {
			intl,
			hasErrors,
			onErrorDismiss,
			onSuccessDismiss,
			onActivationErrorDismiss,
			onReload,
			showSuccessFlag,
			connectedSpaceOrPageName,
			title,
			description,
			showActivationErrorFlag,
		} = this.props;
		// eslint-disable-next-line no-undef
		if (__SERVER__) {
			return null;
		}

		const SUCCESS_FLAG_ID = 'success';
		const ERROR_FLAG_ID = 'error';
		const INFO_FLAG_ID = 'info';

		const handleDismiss = (id: string | number) => {
			if (id === SUCCESS_FLAG_ID) {
				onSuccessDismiss();
			}
			if (id === ERROR_FLAG_ID) {
				onErrorDismiss();
			}
			if (id === INFO_FLAG_ID) {
				onActivationErrorDismiss();
			}
		};

		const flags: any[] = [];
		if (hasErrors) {
			const primaryColor = token('color.icon.warning', colors.Y300);
			const actions = [
				{
					content: intl.formatMessage(messages['generic-error-reload-button']),
					onClick: () => onReload && onReload(),
				},
			];

			flags.push(
				<Flag
					icon={
						<Fragment>
							<WarningIcon label="Warning" color={primaryColor} />
							<ContextualAnalyticsData sourceName="errorFlag" sourceType={MODAL}>
								<ViewTracker />
							</ContextualAnalyticsData>
						</Fragment>
					}
					id={ERROR_FLAG_ID}
					key={ERROR_FLAG_ID}
					actions={actions}
					title={intl.formatMessage(messages['generic-error-heading'])}
					description={intl.formatMessage(messages['generic-error-description'])}
				/>,
			);
		}

		if (showActivationErrorFlag) {
			flags.push(
				<AutoDismissFlag
					testId="confluence-project-pages.info-flag"
					icon={
						<Fragment>
							<InfoIcon label="info" color="currentColor" />
							<ContextualAnalyticsData sourceName="infoFlag" sourceType={MODAL}>
								<ViewTracker />
							</ContextualAnalyticsData>
						</Fragment>
					}
					id={INFO_FLAG_ID}
					key={INFO_FLAG_ID}
					title={intl.formatMessage(messages['generic-error-heading'])}
					description={intl.formatMessage(messages['generic-activation-error-flag-description'])}
				/>,
			);
		}

		if (showSuccessFlag) {
			const primaryColor = token('color.icon.success', colors.G300);

			flags.push(
				<AutoDismissFlag
					testId="confluence-project-pages.success-flag"
					icon={
						<Fragment>
							<SuccessIcon label="Success" color={primaryColor} />
							<ContextualAnalyticsData sourceName="successFlag" sourceType={MODAL}>
								<ViewTracker />
							</ContextualAnalyticsData>
						</Fragment>
					}
					id={SUCCESS_FLAG_ID}
					key={SUCCESS_FLAG_ID}
					title={
						title || intl.formatMessage(this.getSuccessFlagHeading(), { connectedSpaceOrPageName })
					}
					description={
						description ||
						intl.formatMessage(this.getSuccessFlagDescription(), {
							connectedSpaceOrPageName,
						})
					}
				/>,
			);
		}

		return <FlagGroup onDismissed={handleDismiss}>{flags}</FlagGroup>;
	}
}

export default injectIntl(Flags);
